/** @format */
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Grid,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Backdrop,
} from '@mui/material';
import copy from 'copy-to-clipboard';
import createToast from 'components/Toast/Toast';
import { ConfirmDialog, InputV2, LoadingPage, Icons } from 'components';
import KeyGenServices from './KeyGenServices';
import { RadioButtonIcon, RadioButtonIconFill } from 'components/CheckBox/RadioButtonIcon';

const PublicKey = ({ onClose }) => {
  // -- STATES --//
  const [isLoading, setIsLoading] = useState(false);

  const [keyFormat, setKeyFormat] = useState('xml');

  const [publicKey, setPublicKey] = useState(null);
  const [privateKey, setPrivateKey] = useState(null);

  const [publickeyIsCopied, setPublickeyIsCopied] = useState(false);
  const [privatekeyIsCopied, setPrivatekeyIsCopied] = useState(false);
  const [useThisKey, setUseThisKey] = useState(true);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // -- HOOKS --//
  const classes = useStyles();

  useEffect(() => {
    generateKey();
  }, []);

  useEffect(() => {
    generateKey();
  }, [keyFormat]);

  // -- APIS --//
  const generateKey = async () => {
    try {
      setPrivatekeyIsCopied(false);
      setPublickeyIsCopied(false);
      setIsLoading(true);
      const result = await KeyGenServices.generateKey({ keyFormat });
      const data = result?.result;
      setPrivateKey(data?.privateKey);
      setPublicKey(data?.publicKey);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  // -- OTHER FUNCTIONS --//
  const handleUseAndClose = () => {
    if (privatekeyIsCopied && publickeyIsCopied) {
      handleConfirm();
    } else {
      setIsConfirmOpen(true);
    }
  };

  const handleConfirm = () => {
    onClose(publicKey, keyFormat);
    setIsConfirmOpen(false);
  };

  const getNotCopiedMessages = () => {
    let msg = '';
    if (!privatekeyIsCopied && !publickeyIsCopied) {
      msg = 'مقادیر کلیدهای عمومی و خصوصی';
    } else if (!privatekeyIsCopied) {
      msg = 'مقدار کلید خصوصی';
    } else if (!publickeyIsCopied) {
      msg = 'مقدار کلید عمومی';
    }
    return `شما ${msg} را کپی یا دریافت نکرده‌اید، آیا از بستن پنجره کلید‌های ایجاد شده اطمینان دارید؟`;
  };

  const copyItem = async (v, onCopied) => {
    try {
      await copy(v);
      createToast('موفق', 'کلید با موفقیت کپی شد', 'success');

      if (onCopied) onCopied();
    } catch (e) {
      console.log(e);
    }
  };

  const downloadItem = async (data, filename, type = 'text/plain', onDone) => {
    // Function to download data to a file
    const file = new Blob([data], { type });
    if (window.navigator.msSaveOrOpenBlob)
      // IE10+
      window.navigator.msSaveOrOpenBlob(file, filename);
    else {
      // Others
      const a = document.createElement('a');
      const url = URL.createObjectURL(file);
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(function () {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 0);
    }
    if (onDone) onDone();
  };
  return (
    <div className={classes.root}>
      <Backdrop className={classes.backdrop} open={isLoading}>
        <LoadingPage />
      </Backdrop>
      <ConfirmDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirm}
        title="تایید ذخیره کلید‌ها"
        content={getNotCopiedMessages()}
        confirmButtonText="تایید و بستن"
        cancelButtonProps="انصراف"
      />

      {/* Main */}
      <Grid
        container
        justifyContent="flex-start"
        alignItems="flex-end"
        spacing={2}
        style={{ marginBottom: 10 }}
      >
        <Grid item>
          <InputLabel shrink htmlFor="bootstrap-input" className={classes.label}>
            <span className={classes.asterisk}>* &nbsp;</span>
            نوع کلید مورد نظر
          </InputLabel>
          <RadioGroup
            row
            required
            onChange={(e) => setKeyFormat(e?.target?.value)}
            value={keyFormat}
            defaultValue="xml"
          >
            <FormControlLabel
              value="xml"
              control={
                <Radio
                  color="primary"
                  icon={<RadioButtonIcon />}
                  checkedIcon={<RadioButtonIconFill />}
                />
              }
              label="XML"
            />
            <FormControlLabel
              value="pem"
              control={
                <Radio
                  color="primary"
                  icon={<RadioButtonIcon />}
                  checkedIcon={<RadioButtonIconFill />}
                />
              }
              label="PEM"
            />
          </RadioGroup>
        </Grid>
      </Grid>

      <Grid container justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Grid item xs={12} md={6}>
          <InputV2
            errors={{}}
            forceValue={publicKey ?? ''}
            name="publicKey"
            labelStyle={{ width: '100%' }}
            label={
              <Grid container justifyContent="space-between" alignItems="flex-start">
                <Grid item>کلید عمومی</Grid>
                <Grid item style={{ marginInlineStart: 'auto', marginTop: -10 }}>
                  <Button
                    startIcon={<Icons icon="copy" color="#d1d8dc" hover="#303d8f" size="20" />}
                    color="primary"
                    onClick={() => copyItem(publicKey ?? '', () => setPublickeyIsCopied(true))}
                  >
                    کپی‌کردن
                  </Button>
                </Grid>
              </Grid>
            }
            onChangeValue={() => { }}
            fullWidth
            multiline
            dir="ltr"
            mainStyle={{ margin: 0, width: '100%' }}
            inputProps={{ style: { marginBottom: 0, height: 230 } }}
          />
          <Button
            variant="outlined"
            onClick={() =>
              downloadItem(publicKey, `publickey.${keyFormat}`, null, setPublickeyIsCopied(true))
            }
            startIcon={<Icons icon="ReceiveSquare" color="#2B3990" size="23" />}
          >
            دریافت کلید عمومی
          </Button>
        </Grid>
        <Grid item xs={12} md={6}>
          <InputV2
            errors={{}}
            forceValue={privateKey ?? ''}
            name="publicKey"
            labelStyle={{ width: '100%' }}
            label={
              <Grid container justifyContent="space-between" alignItems="flex-start">
                <Grid item>کلید خصوصی</Grid>
                <Grid item style={{ marginInlineStart: 'auto', marginTop: -10 }}>
                  <Button
                    startIcon={<Icons icon="copy" color="#d1d8dc" hover="#303d8f" size="20" />}
                    color="primary"
                    onClick={() => copyItem(privateKey ?? '', () => setPrivatekeyIsCopied(true))}
                  >
                    کپی‌کردن
                  </Button>
                </Grid>
              </Grid>
            }
            onChangeValue={() => { }}
            fullWidth
            multiline
            dir="ltr"
            mainStyle={{ margin: 0, width: '100%' }}
            inputProps={{
              style: { marginBottom: 0, height: 230, overflow: 'auto' },
            }}
          />
          <Button
            onClick={() =>
              downloadItem(privateKey, `privatekey.${keyFormat}`, null, setPrivatekeyIsCopied(true))
            }
            variant="outlined"
            startIcon={<Icons icon="ReceiveSquare" color="#2B3990" size="23" />}
          >
            دریافت کلید خصوصی
          </Button>
        </Grid>
      </Grid>

      <Grid
        container
        xs={12}
        style={{ display: 'flex', flexDirection: 'row-reverse', marginTop: '16px', gap: '15px', }}
      >
        <Button color="primary" variant="contained" onClick={handleUseAndClose}>
          تایید و استفاده از کلید
        </Button>

        <Button variant="contained" onClick={onClose}>
          بستن و انصراف
        </Button>
      </Grid>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(1),
  },
  label: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#546e7a',
    marginBottom: 10,
    '&.MuiInputLabel': {
      '&-shrink': {
        transform: 'translate(0, 1.5px) scale(1)',
      },
    },
  },
  asterisk: {
    color: '#ef5350',
  },
  textArea: {
    width: '100%',
    textAlign: 'right',
    direction: 'rtl',
    padding: theme.spacing(2),
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

export default PublicKey;
